import React, { useState, useEffect } from 'react';
import { 
  TextField, Button, Checkbox, Container, FormControlLabel, 
  InputLabel, Select, MenuItem, Typography, Grid, Box,
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, createTheme, useMediaQuery
} from '@mui/material';
import Error from '../Error/Error';
import Success from '../Success/Success';
import { useTranslation } from 'react-i18next';
import { getArticles, getUnitsOfMeasure, getIngredients, editArticle, editRecipe, getRecipes } from '../../api/axios';
import { useParams, useNavigate} from 'react-router-dom';

const EditArticle = () => {
    const { t } = useTranslation('global');
    const { id } = useParams();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [unitOfMeasure, setUnitOfMeasure] = useState('');
    const [numPortion, setNumPortion] = useState(0);
    const [unitPrice, setUnitPrice] = useState(0);
    const [active, setActive] = useState(false);
    const [button, setButton] = useState(0);

    const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [selectedIngredients, setSelectedIngredients] = useState([]);
    const [ingredientNorms, setIngredientNorms] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [errMsg, setErrMsg] = useState('');
    const [succ, setSucc] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [unitsRes, ingredientsRes, articlesRes, recipesRes] = await Promise.all([
                    getUnitsOfMeasure(),
                    getIngredients(),
                    getArticles(),
                    getRecipes()
                ]);
                
                setUnitsOfMeasure(unitsRes);
                setIngredients(ingredientsRes);
                
                const article = articlesRes.find(e => e.id.toString() === id);
                if(article) {
                    setName(article.name);
                    setActive(Boolean(article.active));
                    setUnitOfMeasure(article.unit_of_measure_id);
                    setNumPortion(article.num_portion);
                    setUnitPrice(article.unit_price);
                    setButton(article.button);
                    
                    // Filter recipes for this article and set selected ingredients/norms
                    const articleRecipes = recipesRes.filter(r => r.article_id.toString() === id);
                    setSelectedIngredients(articleRecipes.map(r => r.ingredient_id));
                    setIngredientNorms(articleRecipes.reduce((acc, recipe) => {
                        acc[recipe.ingredient_id] = recipe.norm_per_portion;
                        return acc;
                    }, {}));
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [id]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!name || !numPortion || !unitPrice || !button) {
            setErrMsg(t("edit.allFieldsAreRequired"));
            return;
        }

        const updatedArticle = {
            id,
            name,
            unit_of_measure_id: unitOfMeasure,
            num_portion: numPortion,
            unit_price: unitPrice,
            active: active ? 1 : 0,
            button
        };

        try {
            // First update the article
            const articleResponse = await editArticle(updatedArticle);
            if (!articleResponse.success) {
                setErrMsg(t("edit.errorDuringEdit"));
                setTimeout(() => setErrMsg(''), 3000);
                return;
            }

            // Then update recipes for each selected ingredient
            const recipePromises = selectedIngredients.map(ingredientId => {
                return editRecipe({
                    article_id: id,
                    ingredient_id: ingredientId,
                    norm_per_portion: ingredientNorms[ingredientId] || 0
                });
            });

            await Promise.all(recipePromises);

            setSucc(t("edit.editSuccessful"));
            setTimeout(() => setSucc(''), 3000);

        } catch (error) {
            setErrMsg(t("edit.errorDuringEdit"));
            setTimeout(() => setErrMsg(''), 3000);
        }
    };

    const theme = createTheme({
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 900,
                lg: 1200,
                xl: 1536,
                xxl: 1650
            },
        },
    });
    const isSmall = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <Container maxWidth="lg" className="container-employee" sx={{ p: 3, flexShrink: 0, mt: 4 }}>
            <Typography variant="h4" sx={{ mb:4 }}>{t("article.edit")}</Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{width: "90%", display: "flex", justifyContent: "center", alignItems: "center"}}>
                {isSmall ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label={t("article.name")}
                                fullWidth
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Select
                                fullWidth
                                value={unitOfMeasure}
                                onChange={(e) => setUnitOfMeasure(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="" disabled>
                                    {t("article.unitOfMeasure")}
                                </MenuItem>
                                {unitsOfMeasure.map((unit) => (
                                    <MenuItem key={unit.id} value={unit.id}>
                                        {unit.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t("article.unitPrice")}
                                fullWidth
                                value={unitPrice}
                                onChange={(e) => setUnitPrice(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t("article.numPortion")}
                                fullWidth
                                value={numPortion}
                                onChange={(e) => setNumPortion(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t("article.button")}
                                fullWidth
                                value={button}
                                onChange={(e) => setButton(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={active}
                                        onChange={(e) => setActive(e.target.checked)}
                                    />
                                }
                                label={t("article.active")}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                {t("article.ingredients")}
                            </Typography>
                            <TextField
                                label={t("article.searchIngredients")}
                                fullWidth
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("article.select")}</TableCell>
                                            <TableCell>{t("article.ingredient")}</TableCell>
                                            <TableCell>{t("article.normPerPortion")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ingredients
                                            .filter(ingredient => 
                                                ingredient.name.toLowerCase().includes(searchTerm.toLowerCase())
                                            )
                                            .map((ingredient) => (
                                            <TableRow key={ingredient.id}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedIngredients.includes(ingredient.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedIngredients([...selectedIngredients, ingredient.id]);
                                                            } else {
                                                                setSelectedIngredients(selectedIngredients.filter(id => id !== ingredient.id));
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>{ingredient.name}</TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={ingredientNorms[ingredient.id] || ''}
                                                        onChange={(e) => setIngredientNorms({
                                                            ...ingredientNorms,
                                                            [ingredient.id]: parseFloat(e.target.value) || 0
                                                        })}
                                                        disabled={!selectedIngredients.includes(ingredient.id)}
                                                        InputProps={{
                                                            inputProps: { min: 0, step: 0.01 }
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                        <Grid item xs={6}>
                            <Button variant="contained" type="submit" fullWidth>
                                {t("edit.save")}
                            </Button>
                            
                        </Grid>
                        <Grid item xs={6}>
                                        <Button
                                        fullWidth
                                            type="button"
                                            variant="contained"
                                            color="error"
                                            onClick={() => { navigate('/new-article') }}
                                        >
                                            {t("editReader.cancel")}
                                        </Button>
                                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("article.name")}
                                        fullWidth
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Select
                                        fullWidth
                                        value={unitOfMeasure}
                                        onChange={(e) => setUnitOfMeasure(e.target.value)}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            {t("article.unitOfMeasure")}
                                        </MenuItem>
                                        {unitsOfMeasure.map((unit) => (
                                            <MenuItem key={unit.id} value={unit.id}>
                                                {unit.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("article.unitPrice")}
                                        fullWidth
                                        value={unitPrice}
                                        onChange={(e) => setUnitPrice(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("article.numPortion")}
                                        fullWidth
                                        value={numPortion}
                                        onChange={(e) => setNumPortion(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("article.button")}
                                        fullWidth
                                        value={button}
                                        onChange={(e) => setButton(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={active}
                                                onChange={(e) => setActive(e.target.checked)}
                                            />
                                        }
                                        label={t("article.active")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={6}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {t("article.ingredients")}
                            </Typography>
                            <TextField
                                label={t("article.searchIngredients")}
                                fullWidth
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("article.select")}</TableCell>
                                            <TableCell>{t("article.ingredient")}</TableCell>
                                            <TableCell>{t("article.normPerPortion")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ingredients
                                            .filter(ingredient => 
                                                ingredient.name.toLowerCase().includes(searchTerm.toLowerCase())
                                            )
                                            .map((ingredient) => (
                                            <TableRow key={ingredient.id}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedIngredients.includes(ingredient.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedIngredients([...selectedIngredients, ingredient.id]);
                                                            } else {
                                                                setSelectedIngredients(selectedIngredients.filter(id => id !== ingredient.id));
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>{ingredient.name}</TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={ingredientNorms[ingredient.id] || ''}
                                                        onChange={(e) => setIngredientNorms({
                                                            ...ingredientNorms,
                                                            [ingredient.id]: parseFloat(e.target.value) || 0
                                                        })}
                                                        disabled={!selectedIngredients.includes(ingredient.id)}
                                                        InputProps={{
                                                            inputProps: { min: 0, step: 0.01 }
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Grid container spacing={2} mt={2}>
                                <Grid item xs={12} sm={6}>
                                    <Button fullWidth variant="contained" type="submit" >
                                        {t("edit.save")}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                        <Button
                                        fullWidth
                                            type="button"
                                            variant="contained"
                                            color="error"
                                            onClick={() => { navigate('/new-article') }}
                                        >
                                            {t("editReader.cancel")}
                                        </Button>
                                        </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {errMsg && <Error message={errMsg} onClose={() => setErrMsg('')} />}
            {succ && <Success message={succ} onClose={() => setSucc('')} />}
        </Container>
    );
};

export default EditArticle;
